import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { streamPost } from "@/lib/stream";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import AiText from "@/components/AiText";
import TeacherAvatar from "@/components/TeacherAvatar";
import { ArrowLeft, Sparkles, RefreshCw, CheckCircle2, HelpCircle, Image as ImageIcon, Volume2, Loader2 } from "lucide-react";

function playBase64Audio(b64) {
  if (!b64) return;
  new Audio(`data:audio/mp3;base64,${b64}`).play().catch(() => {});
}

export default function TopicDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [diagram, setDiagram] = useState(null);
  const [diagramBusy, setDiagramBusy] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/topics/${id}`).then(({ data }) => setTopic(data)).catch(() => navigate("/konular"));
  }, [id, navigate]);

  const explain = async (simplify = false) => {
    setLoading(true);
    setText("");
    setDone(false);
    try {
      await streamPost("/ai/explain", { topic_id: id, simplify }, (delta) => {
        setText((prev) => prev + delta);
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      });
      setDone(true);
    } catch (err) {
      if (err.status === 402) {
        toast.error("Bu özellik için Premium abonelik gerekli");
        navigate("/abonelik");
      } else {
        toast.error(err.message || "Bir hata oluştu");
      }
    } finally {
      setLoading(false);
    }
  };

  const markComplete = async () => {
    await api.post("/topics/complete", { topic_id: id });
    toast.success("Konu tamamlandı olarak işaretlendi! 🎉");
    setTopic({ ...topic, completed: true });
  };

  const makeDiagram = async () => {
    setDiagramBusy(true);
    try {
      const { data } = await api.post("/ai/diagram", { prompt: `${topic.subject} - ${topic.title}` });
      setDiagram(data.image);
    } catch (err) {
      if (err.response?.status === 402) navigate("/abonelik");
      else toast.error(err.response?.data?.detail || "Görsel oluşturulamadı");
    } finally {
      setDiagramBusy(false);
    }
  };

  const speak = async () => {
    setSpeaking(true);
    try {
      const { data } = await api.post("/ai/speak", { prompt: text.slice(0, 3800) });
      playBase64Audio(data.audio_base64);
    } catch {
      toast.error("Ses oluşturulamadı");
    } finally {
      setSpeaking(false);
    }
  };

  if (!topic) return <div className="text-center py-20 text-4xl animate-bounce">🦉</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <TeacherAvatar
        message={text}
        typing={loading}
        idle={`${topic.title} konusunu öğrenmeye hazır mısın? "Nokta Öğretmen anlatsın" butonuna bas! 👇`}
      />
      <button onClick={() => navigate("/konular")} data-testid="back-btn" className="flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600 font-semibold">
        <ArrowLeft className="w-4 h-4" /> Konulara dön
      </button>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <p className="text-xs font-bold text-indigo-600 uppercase tracking-wide">{topic.subject}</p>
        <h1 className="font-display text-2xl font-bold text-slate-900 mt-1">{topic.title}</h1>
        <p className="text-slate-500 text-sm mt-2">{topic.description}</p>

        {!text && !loading && (
          <Button data-testid="explain-btn" onClick={() => explain(false)} className="mt-5 bg-indigo-600 hover:bg-indigo-700 rounded-xl font-bold py-6 px-6">
            <Sparkles className="w-5 h-5 mr-2" /> Nokta Öğretmen anlatsın
          </Button>
        )}
      </div>

      {(text || loading) && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm animate-float-up no-copy" data-testid="explanation-box"
          onContextMenu={(e) => e.preventDefault()} onCopy={(e) => e.preventDefault()} onCut={(e) => e.preventDefault()}>
          <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-100">
            <img src="/teacher-avatar.png" alt="" className="w-10 h-10 object-cover object-top rounded-full ring-2 ring-rose-100" />
            <div>
              <p className="font-bold text-slate-900">Nokta Öğretmen</p>
              <p className="text-xs text-slate-400">{loading ? "yazıyor..." : "ders anlatımı"}</p>
            </div>
          </div>
          <AiText text={text} />
          {loading && <span className="inline-block w-2 h-4 bg-indigo-500 animate-pulse ml-1" />}
          <div ref={bottomRef} />

          {diagram && (
            <img src={diagram} alt="Konu görseli" data-testid="topic-diagram" className="mt-5 rounded-xl border border-slate-200 max-w-full" />
          )}

          {done && (
            <div className="mt-6 pt-5 border-t border-slate-100 flex flex-wrap gap-3">
              <Button data-testid="simplify-btn" onClick={() => explain(true)} variant="outline" className="rounded-xl font-bold border-2">
                <HelpCircle className="w-4 h-4 mr-1" /> Anlamadım, basitleştir
              </Button>
              <Button data-testid="speak-btn" onClick={speak} disabled={speaking} className="rounded-xl font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm">
                {speaking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Volume2 className="w-4 h-4 mr-1" />} Sesli dinle
              </Button>
              <Button data-testid="topic-diagram-btn" onClick={makeDiagram} disabled={diagramBusy} variant="outline" className="rounded-xl font-bold border-2 text-indigo-600 border-indigo-200">
                {diagramBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-1" />} Görsel oluştur
              </Button>
              {!topic.completed && (
                <Button data-testid="complete-topic-btn" onClick={markComplete} className="bg-emerald-500 hover:bg-emerald-600 rounded-xl font-bold ml-auto">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Öğrendim
                </Button>
              )}
              {topic.completed && (
                <span className="ml-auto flex items-center gap-1 text-emerald-600 font-bold text-sm"><CheckCircle2 className="w-5 h-5" /> Tamamlandı</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
